import { Instance, types, getRoot } from "mobx-state-tree";
import { Composition, Scene } from "./types";
import { listCompositions } from "../jsxProviders";
import { RootInstance } from "./root";
import { getUpdatedAt } from "./utils/sceneUpdateAt";

export const CompositionStore = types
  .model({
    selectedCompositionsId: types.optional(types.array(types.number), []),
    queuedCompositions: types.optional(types.array(types.number), []),
    allCompositions: types.optional(
      types.array(types.frozen<Composition>()),
      []
    ),
    compositionCurrentlyRendering: types.optional(
      types.model({ id: types.number, name: types.string }),
      { id: 0, name: "" }
    ),
    renderedCompositionsSuccess: types.optional(types.array(types.number), []),
    renderedCompositionsFail: types.optional(types.array(types.number), []),
    trigger: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get isCurrentlyRenderingFirstInQueue() {
      if (!self.compositionCurrentlyRendering.id) return false;
      if (this.sortedQueuedCompositions.length === 0) return false;
      return (
        this.sortedQueuedCompositions[0] === self.compositionCurrentlyRendering.id
      );
    },
    // Get the same order as the filtered compositions and not by last added
    get sortedQueuedCompositions() {
      const { sortedFilteredCompositions } = this;
      return [...self.queuedCompositions].sort((a, b) => {
        return (
          sortedFilteredCompositions.findIndex((c) => c.id === a) -
          sortedFilteredCompositions.findIndex((c) => c.id === b)
        );
      });
    },
    get allRenderedCompositions() {
      return self.renderedCompositionsFail.concat(
        self.renderedCompositionsSuccess
      );
    },
    get selectedCompositionsName() {
      return self.selectedCompositionsId.map((id) => {
        return (
          self.allCompositions.find((composition) => composition.id === id)
            ?.name || ""
        );
      });
    },
    get sortedFilteredCompositions() {
      const ignoreComps = ["bm_", "tempConverterCompm", "__bodymovin_copy"];
      return (
        self.allCompositions
          .map((c) => ({ name: c.name, id: c.id }))
          .filter((item) => !ignoreComps.some((e) => item.name.indexOf(e) > -1))
          /**
           *
           * @param a Element to compare
           * @param b Element to compare
           * @returns 0 | -1 | 1 . 0 If the order should not change, -1 if a should come before b. 1 if b should come before a.
           */
          .sort((a, b) => {
            const { renderStore, uiStore, projectStore } =
              getRoot<RootInstance>(self);
            const { filterComposition } = uiStore;
            const scene = (name: string): Scene =>
              projectStore.videoParts?.find((scene) => scene?.name === name);

            // If true, sort by selected and then by time
            if (filterComposition || renderStore.isRendering) {
              if (
                // If one of the compositions is selected and the other is not
                self.selectedCompositionsId.some((comp) => comp === a.id) !==
                self.selectedCompositionsId.some((comp) => comp === b.id)
              ) {
                return self.selectedCompositionsId.some((comp) => comp === a.id)
                  ? -1
                  : 1;
              }
            }

            const aDate = getUpdatedAt(scene(a.name?.toString() || ""));
            const bDate = getUpdatedAt(scene(b.name?.toString() || ""));
            if (aDate === bDate) return 0; // If they are equal
            if (typeof aDate === "string") {
              if (typeof bDate === "string") {
                return new Date(bDate).getTime() - new Date(aDate).getTime(); // If they are both strings
              } else {
                return -1; // If a is a string, b is not
              }
            } else if (typeof bDate === "string") {
              return 1; // If b is a string and a is not
            }
            return bDate === null ? 1 : -1; // Sort by the composition that is rendered, the one that is not rendered comes after
          })
      );
    },
  }))
  .actions((self) => ({
    updateCompositionsList(comps: Composition[]) {
      self.allCompositions.replace(comps);
    },
  }))
  .actions((self) => ({
    removeRenderedComposition(id: number) {
      if (self.renderedCompositionsFail.includes(id)) {
        self.renderedCompositionsFail.splice(
          self.renderedCompositionsFail.indexOf(id),
          1
        );
      } else if (self.renderedCompositionsSuccess.includes(id)) {
        self.renderedCompositionsSuccess.splice(
          self.renderedCompositionsSuccess.indexOf(id),
          1
        );
      }
      self.trigger = !self.trigger;
    },
    fillQueuedCompositions() {
      self.queuedCompositions.replace(self.selectedCompositionsId);
    },
    setQueuedCompositions(id: number | null) {
      // Logic to add or remove the id from the array
      if (!id) return self.queuedCompositions.replace([]);
      !self.queuedCompositions.includes(id) && self.queuedCompositions.push(id);
    },
    removeQueuedComposition(id: number) {
      // Logic to add or remove the id from the array
      self.queuedCompositions.includes(id) &&
        self.queuedCompositions.splice(self.queuedCompositions.indexOf(id), 1);
    },
    setRenderedCompositions(id: number | null, success: boolean = true) {
      // If the ID does not exist, reset the model for a new render
      if (!id) {
        self.renderedCompositionsSuccess.replace([]);
        self.compositionCurrentlyRendering = { name: "", id: 0 };
        return self.renderedCompositionsFail.replace([]);
      }
      const modelName:
        | "renderedCompositionsSuccess"
        | "renderedCompositionsFail" = `renderedCompositions${
        success ? "Success" : "Fail"
      }`;
      const newArray = self[modelName].map((comp) => comp);
      newArray.push(id);
      self[modelName].replace(newArray);
      self.trigger = !self.trigger;
    },
    setCompositionCurrentlyRendering(selectedComp: {
      id: number;
      name: string;
    }) {
      const { name, id } = selectedComp;
      self.compositionCurrentlyRendering.name = name;
      self.compositionCurrentlyRendering.id = id;
    },
    setSelectedCompositions(id: number | null) {
      // Logic to add or remove the id from the array
      if (!id) {
        //Reset
        self.selectedCompositionsId.replace([]);
        this.setQueuedCompositions(null);
        this.setRenderedCompositions(null);
      } else {
        this.removeRenderedComposition(id);
        self.selectedCompositionsId.includes(id)
          ? self.selectedCompositionsId.splice(
              self.selectedCompositionsId.indexOf(id),
              1
            ) && this.removeQueuedComposition(id)
          : self.selectedCompositionsId.push(id);
      }
    },
    async listAllComps() {
      try {
        const list: any = await listCompositions();
        self.updateCompositionsList(list);
      } catch (error) {
        console.log(error);
      }
    },
  }));

export type ICompositionsModel = Instance<typeof CompositionStore>;
